import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Autocomplete, TextField } from '@mui/material';

const MIN_SEARCH_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 1000;

const alphaOnly = (input) => (input || '').replace(/[^A-Za-z]/g, '');

const LeakLocationAutocomplete = ({
    leakLocations = [],
    leakLocationCategories = [],
    leakLocationId,
    onChange,
    label,
    required = false,
    error = false,
    helperText = '',
    disabled = false,
    placeholder = 'Search or Select',
    fullWidth = true,
    sx,
}) => {
    const sortedOptions = useMemo(() => {
        const categoryNameById = new Map(leakLocationCategories.map((c) => [c.id, c.name]));
        return leakLocations
            .map((ll) => {
                const categoryName = categoryNameById.get(ll.category) ?? 'Unknown';
                return {
                    id: ll.id,
                    categoryId: ll.category,
                    categoryName,
                    subLocationName: ll.name,
                    name: `${categoryName} — ${ll.name}`,
                };
            })
            .sort((a, b) => (
                a.categoryName.localeCompare(b.categoryName)
                || a.subLocationName.localeCompare(b.subLocationName)
            ));
    }, [leakLocations, leakLocationCategories]);

    const selectedOption = sortedOptions.find((o) => o.id === leakLocationId) ?? null;

    const [inputValue, setInputValue] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const debounceRef = useRef(null);

    useEffect(() => {
        if (selectedOption) {
            setInputValue(selectedOption.name);
        }
    }, [selectedOption?.id]);

    useEffect(() => {
        clearTimeout(debounceRef.current);

        const cleaned = alphaOnly(inputValue);
        const isSelectedLabel = selectedOption && selectedOption.name === inputValue;
        if (isSelectedLabel || cleaned.length < MIN_SEARCH_CHARS) {
            setAppliedQuery('');
            return;
        }

        debounceRef.current = setTimeout(() => {
            setAppliedQuery(cleaned.toLowerCase());
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(debounceRef.current);
    }, [inputValue, selectedOption]);

    const filterOptions = useCallback(
        (opts) => {
            if (!appliedQuery) return opts;
            return opts.filter(
                (o) =>
                    o.categoryName.toLowerCase().includes(appliedQuery) ||
                    o.subLocationName.toLowerCase().includes(appliedQuery)
            );
        },
        [appliedQuery]
    );

    const handleChange = (_, newOption) => {
        onChange({
            leakLocationId: newOption?.id ?? null,
            leakLocationCategoryId: newOption?.categoryId ?? null,
            option: newOption ?? null,
        });
    };

    const handleInputChange = (_, next, reason) => {
        if (reason === 'reset' || reason === 'clear') {
            setInputValue(next ?? '');
            return;
        }
        setInputValue(alphaOnly(next));
    };

    return (
        <Autocomplete
            fullWidth={fullWidth}
            options={sortedOptions}
            groupBy={(option) => option.categoryName}
            getOptionLabel={(option) => option?.name ?? ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={selectedOption}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            filterOptions={filterOptions}
            disabled={disabled}
            autoHighlight
            blurOnSelect
            sx={sx}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    {option.subLocationName}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={!!error}
                    helperText={helperText}
                    placeholder={placeholder}
                />
            )}
        />
    );
};

export default LeakLocationAutocomplete;
